import "./IndividualPricing.css";
// import { Link } from "react-router-dom";
import TutorImage from "../Images/soloTutoringImage.jpg";
import React, { useState, useRef, useEffect } from "react";
import logger from "../Components/Logger";
import { LevelName } from "../Components/LevelName";

// Source code https://stackoverflow.com/questions/59595700/how-to-make-a-react-component-fade-in-on-scroll-using-intersectionobserver-but 
// + ChatGPT fixing small bugs in layout.
const FadeInSection = ({ children }) => {
  const domRef = useRef(null);
  const [isVisible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        setVisible(true);
        observer.unobserve(domRef.current);
        logger.info("FadeInSection is now visible on the screen.");
      }
    });

    if (domRef.current) {
      observer.observe(domRef.current);
      logger.info("IntersectionObserver is now observing the FadeInSection component.");
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={domRef}
      className={`fade-section ${isVisible ? "is-visible" : ""}`}
    >
      {children}
    </section>
  );
};

const CACHE_KEY = "a1app_pricing_cache";
const CACHE_TTL = 60 * 60 * 1000; // 1 hour

export function IndividualPricing({ t }) {
  const [prices, setPrices] = useState(null);

  useEffect(() => {
    // ✅ Try localStorage cache first — no Firestore read
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL) {
          logger.info("Pricing loaded from localStorage cache.");
          setPrices(data);
          return;
        }
      }
    } catch (_) {}

    // Cache miss or expired — fetch from server
    fetch("/.netlify/functions/getPricing")
      .then((res) => res.json())
      .then((data) => {
        setPrices(data);
        try {
          localStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
          logger.info("Pricing fetched and saved to localStorage cache.");
        } catch (_) {}
      })
      .catch((err) => {
        logger.error("Erreur chargement prix:", err);
      });
  }, []);

  return (
    <div className="soloPricingPage">
      <aside className="soloSidebarMenu">
        <section className="soloSection">
          <div className="soloInformationBox">
            <h2 className="soloDashboardTitle">{t.IndividualTutoringOverview}</h2>
          </div>
        </section>

        <div className="soloNavigationSections">
          <button className="soloNavButton" onClick={() => window.scrollTo({ top: 100, behavior: "smooth" })}>
            <span>📅</span> {t.Schedule}
          </button>
          <button className="soloNavButton" onClick={() => window.scrollTo({ top: 800, behavior: "smooth" })}>
            <span>👦</span> <LevelName level="elementary" />
          </button>
          <button className="soloNavButton" onClick={() => window.scrollTo({ top: 1300, behavior: "smooth" })}>
            <span>🎓</span> <LevelName level="highschool" />
          </button>
          <button className="soloNavButton" onClick={() => window.scrollTo({ top: 1800, behavior: "smooth" })}>
            <span>👨‍🏫</span> <LevelName level="cegep" />
          </button>
          <div className="soloSidebarDivider"></div>
        </div>
      </aside>

      <main className="soloMainPage">
        <div className="soloHeaderImage">
          <img src={TutorImage} width={750} height={300} alt="TutoringExample" />
        </div>

        <div>
          <FadeInSection>
            <section>
              <div className="soloTutoringHours">
                <h4>{t.IndividualTutoringHours} 📚</h4>
                <hr className="soloScheduleDivider" />
                <p className="soloExtraInfo">{t.WeekendsOnly}</p>
                <div className="soloScheduleGrid">
                  <div className="soloScheduleBox">
                    <h4>{t.Availability} :</h4>
                    <div className="soloNavigationHours">
                      <p>{t.Saturday}: 1:00 PM - 3:00 PM</p>
                      <p>{t.Sunday}: 1:00 PM - 3:00 PM</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </FadeInSection>
        </div>

        <section className="soloPricingSection">
          <h2 className="soloPricingTitle">{t.TutoringPricesTitle}</h2>
          <p className="soloPricingSubtitle">{t.PricingSubtitle}</p>

          <div className="soloPricingGrid">
            <div className="soloPricingCard">
              <div className="soloPricingHeader">
                <h3><LevelName level="elementary" /></h3>
                <p className="soloPricingPrice">
                  <span className="currency">$</span>
                  <span>{prices ? prices.primary_1v1 : "..."}</span>
                  <small className="period"> / {t.Hour}</small>
                </p>
              </div>
              <FadeInSection>
                <div className="soloPricingBody">
                  <p className="soloPricingPlan">{t.BasicPlan}</p>
                  <ul className="soloPricingList">
                    <li>{t.IndividualTutoring}</li>
                    <li>{t.HomeworkSupport}</li>
                  </ul>
                  <div className="soloPricingSubjects">
                    <h4>{t.TutoringSubjectsLabel}:</h4>
                    <p>{t.ElementarySubjectsList}</p>
                  </div>
                </div>
              </FadeInSection>
            </div>

            <div className="soloPricingCard premium">
              <div className="soloPricingHeader">
                <h3><LevelName level="highschool" /></h3>
                <p className="soloPricingPrice">
                  <span className="currency">$</span>
                  <span>{prices ? prices.secondary_1v1 : "..."}</span>
                  <small className="period"> / {t.Hour}</small>
                </p>
              </div>
              <FadeInSection>
                <div className="soloPricingBody">
                  <p className="soloPricingPlan">{t.BasicPlan}</p>
                  <ul className="soloPricingList">
                    <li>{t.IndividualTutoring}</li>
                    <li>{t.ExamPrepAndSupport}</li>
                    <li>{t.ProgressUpdate}</li>
                  </ul>
                  <div className="soloPricingSubjects">
                    <h4>{t.TutoringSubjectsLabel}:</h4>
                    <p>{t.HighSchoolSubjectsList}</p>
                  </div>
                </div>
              </FadeInSection>
            </div>

            <div className="soloPricingCard premium">
              <div className="soloPricingHeader">
                <h3><LevelName level="cegep" /></h3>
                <p className="soloPricingPrice">
                  <span className="currency">$</span>
                  <span>{prices ? prices.college_1v1 : "..."}</span>
                  <small className="period"> / {t.Hour}</small>
                </p>
              </div>
              <FadeInSection>
                <div className="soloPricingBody">
                  <p className="soloPricingPlan">{t.BasicPlan}</p>
                  <ul className="soloPricingList">
                    <li>{t.IndividualTutoring}</li>
                    <li>{t.ExamPrepAndSupport}</li>
                  </ul>
                  <div className="soloPricingSubjects">
                    <h4>{t.TutoringSubjectsLabel}:</h4>
                    <p>{t.CollegeSubjectsList}</p>
                  </div>
                </div>
              </FadeInSection>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}